import {
  defaultProductIds,
  type Store,
  type Evidence,
  type Dashboard,
  type VisitInput,
  type SearchFilters,
  type SavedSearch,
  type Buddy,
} from "@little-finds/core";
const epoch = Date.now();
export const demoStores: Store[] = [
  [
    "Juniper Toy Shop",
    "1842 N. Meadow Avenue",
    41.916,
    -87.674,
    ["NeeDoh", "Jellycat"],
  ],
  [
    "The Little Paper Co.",
    "2124 W. Clover Street",
    41.92,
    -87.661,
    ["Jellycat"],
  ],
  [
    "Daydream General Store",
    "1530 N. Willow Avenue",
    41.909,
    -87.654,
    ["NeeDoh", "Jellycat"],
  ],
  ["Small Wonders", "927 W. Fern Street", 41.925, -87.647, ["Jellycat"]],
  ["Hello, Sunshine", "2440 N. Acorn Avenue", 41.937, -87.665, ["NeeDoh"]],
  ["Pebble & Pine", "1620 W. Garden Street", 41.933, -87.68, ["Jellycat"]],
  [
    "Good Day Goods",
    "810 N. Little Lane",
    41.901,
    -87.673,
    ["NeeDoh", "Jellycat"],
  ],
  ["The Cozy Corner", "1208 W. Maple Street", 41.94, -87.648, ["Jellycat"]],
].map((r, i) => ({
  id: `20000000-0000-4000-8000-${String(i + 1).padStart(12, "0")}`,
  name: r[0] as string,
  address: r[1] as string,
  city: "Chicago",
  state: "IL",
  zip: "60614",
  latitude: r[2] as number,
  longitude: r[3] as number,
  timezone: "America/Chicago",
  brands: r[4] as string[],
  demo: true,
}));
function observation(
  n: number,
  store: number,
  product: number,
  hours: number,
  outcome: Evidence["outcome"] = "saw_it",
  kind: Evidence["source_kind"] = "community",
): Evidence {
  return {
    id: `demo-evidence-${n}`,
    store_id: demoStores[store].id,
    product_id: `10000000-0000-4000-8000-${String(product).padStart(12, "0")}`,
    contributor_id: `demo-collector-${n}`,
    source_kind: kind,
    observed_at: new Date(epoch - hours * 3600000).toISOString(),
    received_at: new Date(epoch - hours * 3600000).toISOString(),
    outcome,
    moderation: "approved",
    variant: product === 5 ? "Original size" : undefined,
    demo: true,
  };
}
export const demoEvidence: Evidence[] = [
  observation(1, 0, 1, 1.5),
  observation(2, 0, 5, 2),
  observation(3, 0, 12, 2),
  observation(4, 1, 8, 3),
  observation(5, 1, 9, 3),
  observation(6, 2, 2, 0.5),
  observation(7, 2, 4, 0.5),
  observation(8, 3, 6, 7),
  observation(9, 4, 3, 6),
  observation(10, 5, 11, 10),
  observation(11, 6, 7, 15),
  observation(12, 7, 10, 31),
  observation(13, 7, 9, 120, "saw_it", "public_mention"),
  observation(14, 1, 5, 4),
  observation(15, 1, 5, 1, "couldnt_find"),
];
export const demoFilters: SearchFilters = {
  product_ids: defaultProductIds,
  category_ids: ["jellycat_bag_charm"],
  center: { latitude: 41.92, longitude: -87.663 },
  radius_miles: 25,
  zoom: 12,
  layers: { older: false, mentions: false, stockists: false },
};
type Award = { key: string; active: boolean; consumed: boolean };
type DemoState = {
  dashboard: Dashboard;
  visits: (VisitInput & { id: string; status: string })[];
  evidence: Evidence[];
  feed_requests: string[];
  awards: Award[];
};
const key = "little-finds:demo:v2";
const initial: DemoState = {
  dashboard: {
    buddy: { available_treats: 2, total_fed: 4, pending_treats: 0 },
    saved_searches: [],
    notifications: [],
    is_admin: true,
    pending_visits: 0,
  },
  visits: [],
  evidence: demoEvidence,
  feed_requests: [],
  awards: Array.from({ length: 6 }, (_, i) => ({
    key: `starter-${i}`,
    active: true,
    consumed: i < 4,
  })),
};
const awardKey = (v: VisitInput) =>
  `${v.store_id}:${new Intl.DateTimeFormat("en-CA", { timeZone: demoStores.find((s) => s.id === v.store_id)?.timezone ?? "America/Chicago" }).format(new Date(v.observed_at))}`;
function derive(s: DemoState) {
  const pending = s.visits.filter((v) => v.status === "pending");
  s.dashboard.pending_visits = pending.length;
  s.dashboard.buddy = {
    available_treats: s.awards.filter((a) => a.active && !a.consumed).length,
    total_fed: Math.min(
      30,
      s.awards.filter((a) => a.active && a.consumed).length,
    ),
    pending_treats: new Set(
      pending
        .filter((v) => !s.awards.some((a) => a.key === awardKey(v) && a.active))
        .map(awardKey),
    ).size,
  };
  return s;
}
export function readDemo(): DemoState {
  try {
    const v = localStorage.getItem(key);
    return derive(v ? JSON.parse(v) : structuredClone(initial));
  } catch {
    return structuredClone(initial);
  }
}
export function writeDemo(state: DemoState) {
  localStorage.setItem(key, JSON.stringify(derive(state)));
}
export function demoSubmit(visit: VisitInput) {
  const s = readDemo(),
    existing = s.visits.find((v) => v.request_id === visit.request_id);
  if (existing) return existing;
  if (
    !demoStores.some((s) => s.id === visit.store_id) ||
    !visit.observations.length
  )
    throw new Error("Choose a shop and a product.");
  s.visits.push({ ...visit, id: visit.request_id, status: "pending" });
  writeDemo(s);
  return s.visits.at(-1)!;
}
export function demoModerate(id: string, decision: string) {
  const s = readDemo(),
    v = s.visits.find((v) => v.id === id);
  if (!v || v.status === decision) return;
  if (!["approved", "rejected"].includes(decision))
    throw new Error("Invalid review decision");
  v.status = decision;
  s.evidence = s.evidence.filter((e) => !e.id.startsWith(`${id}:`));
  if (decision === "approved")
    for (const [i, o] of v.observations.entries())
      s.evidence.push({
        id: `${id}:${i}`,
        store_id: v.store_id,
        product_id: o.product_id,
        source_kind: "community",
        contributor_id: "demo-you",
        observed_at: v.observed_at,
        received_at: new Date().toISOString(),
        outcome: o.outcome,
        moderation: "approved",
        variant: o.variant,
        note: v.note,
        demo: true,
      });
  const k = awardKey(v),
    active = s.visits.some(
      (row) => awardKey(row) === k && row.status === "approved",
    ),
    award = s.awards.find((a) => a.key === k);
  if (award) award.active = active;
  else if (active) s.awards.push({ key: k, active: true, consumed: false });
  s.dashboard.notifications.unshift({
    id: crypto.randomUUID(),
    title:
      decision === "approved"
        ? "Your little find was approved"
        : "Sighting reviewed",
    body:
      decision === "approved"
        ? "Thanks for helping the next collector. Check your treat jar!"
        : "This demo report was not accepted.",
    created_at: new Date().toISOString(),
  });
  writeDemo(s);
}
export function demoFeed(request: string): Buddy {
  const s = readDemo();
  if (!s.feed_requests.includes(request) && s.dashboard.buddy.total_fed < 30) {
    const award = s.awards.find((a) => a.active && !a.consumed);
    if (award) {
      award.consumed = true;
      s.feed_requests.push(request);
      writeDemo(s);
    }
  }
  return derive(s).dashboard.buddy;
}
export function demoSave(search: SavedSearch) {
  const s = readDemo();
  const v = { ...search, id: search.id ?? crypto.randomUUID() };
  s.dashboard.saved_searches = [
    ...s.dashboard.saved_searches.filter((x) => x.id !== v.id),
    v,
  ];
  writeDemo(s);
  return v;
}
